import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, ImagePlus, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const readPhoto = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const scale = Math.min(1, 1200 / img.width);
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.7));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const empty = { nom: "", telephone: "", email: "", ville: "", type_projet: "", budget: "", message: "" };

export default function DevisForm() {
  const [form, setForm] = useState(empty);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3 - photos.length);
    for (const f of files) {
      try {
        const data = await readPhoto(f);
        setPhotos((p) => [...p, data]);
      } catch {
        toast.error("Image illisible", { description: `${f.name} n'a pas pu être chargée.` });
      }
    }
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/devis`, { ...form, photos });
      toast.success("Votre demande a bien été envoyée.", { description: "Merci pour votre demande. DMZ Normandie reviendra vers vous rapidement." });
      setForm(empty);
      setPhotos([]);
    } catch {
      toast.error("Une erreur est survenue lors de l'envoi de votre demande.", { description: "Vous pouvez nous contacter directement par téléphone au 07 45 04 91 89." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-white/10 bg-[#0D0D0D] p-8 md:p-10" data-testid="devis-form">
      <p className="font-serif-display font-light text-2xl md:text-3xl tracking-tight text-[#F5F5F0] mb-8">
        Demande de <em className="text-accent-sand">devis gratuit</em>
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input
          type="text"
          name="entreprise"
          value={form.entreprise || ""}
          onChange={set("entreprise")}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="hidden"
          style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }}
          data-testid="devis-honeypot"
        />
        <input required minLength={2} placeholder="Nom *" value={form.nom} onChange={set("nom")} className="input-dark" data-testid="devis-nom-input" />
        <input required minLength={6} placeholder="Téléphone *" value={form.telephone} onChange={set("telephone")} className="input-dark" data-testid="devis-telephone-input" />
        <input required type="email" placeholder="E-mail *" value={form.email} onChange={set("email")} className="input-dark" data-testid="devis-email-input" />
        <input placeholder="Ville" value={form.ville} onChange={set("ville")} className="input-dark" data-testid="devis-ville-input" />
        <select required value={form.type_projet} onChange={set("type_projet")} className={`input-dark appearance-none ${form.type_projet ? "" : "text-zinc-600"}`} data-testid="devis-type-select">
          <option value="" disabled>Type de projet *</option>
          <option value="maconnerie">Maçonnerie générale</option>
          <option value="murs">Construction de murs</option>
          <option value="fondations">Fondations</option>
          <option value="dalles_beton">Dalles et béton</option>
          <option value="renovation">Rénovation</option>
          <option value="extension">Extension de maison</option>
          <option value="ouverture">Ouverture de mur porteur</option>
          <option value="gros_oeuvre">Gros œuvre</option>
          <option value="amenagement">Aménagement extérieur</option>
          <option value="autre">Autre</option>
        </select>
        <select value={form.budget} onChange={set("budget")} className={`input-dark appearance-none ${form.budget ? "" : "text-zinc-600"}`} data-testid="devis-budget-select">
          <option value="">Budget approximatif (facultatif)</option>
          <option value="moins_5000">Moins de 5 000 €</option>
          <option value="5000_15000">5 000 – 15 000 €</option>
          <option value="15000_50000">15 000 – 50 000 €</option>
          <option value="plus_50000">Plus de 50 000 €</option>
        </select>
      </div>
      <textarea
        required
        minLength={10}
        rows={5}
        placeholder="Décrivez votre projet en quelques lignes… *"
        value={form.message}
        onChange={set("message")}
        className="input-dark mt-4 resize-none"
        data-testid="devis-message-textarea"
      />

      <div className="mt-4">
        <label className="flex items-center gap-3 border border-dashed border-white/15 px-4 py-4 cursor-pointer text-sm font-light text-zinc-500 hover:border-[#C4A484] transition-colors" data-testid="devis-photos-upload">
          <ImagePlus size={18} className="text-[#C4A484]" />
          Ajouter des photos de votre projet (3 max)
          <input type="file" accept="image/*" multiple className="hidden" onChange={addPhotos} disabled={photos.length >= 3} />
        </label>
        {photos.length > 0 && (
          <div className="flex gap-3 mt-3" data-testid="devis-photos-preview">
            {photos.map((p, i) => (
              <div key={i} className="relative w-20 h-20">
                <img src={p} alt={`Photo ${i + 1}`} className="w-full h-full object-cover border border-white/10" />
                <button
                  type="button"
                  onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                  className="absolute -top-2 -right-2 w-5 h-5 bg-[#C4A484] text-[#0A0A0A] rounded-full flex items-center justify-center"
                  data-testid={`devis-photo-remove-${i}`}
                  aria-label="Retirer"
                >
                  <X size={11} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <button type="submit" disabled={loading} className="btn-sable w-full mt-6" data-testid="devis-submit-button">
        {loading ? (
          <>
            <Loader2 size={16} className="animate-spin" /> Envoi en cours…
          </>
        ) : (
          <>
            Envoyer ma demande <ArrowRight size={16} />
          </>
        )}
      </button>
      <p className="mt-4 text-xs font-light text-zinc-600 text-center">Décrivez-nous votre projet. Nous vous recontacterons afin d'échanger sur vos besoins et déterminer les prochaines étapes.</p>
    </form>
  );
}
